import type { InquiryResponse } from 'backend/types'
import { execSearchQuery } from '@/lib/search-utils'
import { InquirySearchParser } from './inquiry-search-parser'

const SEARCHABLE_KEYS: (keyof InquiryResponse)[] = [
  'deal_name',
  'customer_name',
  'ano',
  'salesforce_project_name',
  'contact_name',
  'coordination_member_name',
  'sales_member_name',
  'service_manager_name',
  'primary_division_name',
  'overview',
  'requirements',
  'industry_name',
  'station',
  'inquiry_assign_status_name',
  'inquiry_close_reason',
  'start_on',
  'end_on',
  'created_by_name',
]

const NAME_KEYS = new Set<keyof InquiryResponse>([
  'customer_name',
  'contact_name',
  'coordination_member_name',
  'sales_member_name',
  'service_manager_name',
  'created_by_name',
])

/**
 * 引合をカスタム検索クエリでフィルタリング
 */
export function filterInquiries(
  inquiries: InquiryResponse[],
  searchText: string,
  currentMemberId?: number | null,
  currentDivisionId?: number | null,
): InquiryResponse[] {
  const search = new InquirySearchParser()
  search.parse(searchText)

  if (!search.enabled()) return inquiries

  return inquiries.filter((item) => customFilter(item, search, currentMemberId, currentDivisionId))
}

/**
 * 開始日が検索の開始月（YYYY/MM）に一致するか。開始日が無い引合は一致しない
 */
function matchesStartMonth(startOn: string | null, startMonth: string): boolean {
  if (!startOn) return false
  const [year, month] = startMonth.split('/')
  if (!year || !month) return true
  return startOn.slice(0, 7) === `${year}-${month.padStart(2, '0')}`
}

/**
 * 個別引合に対するカスタムフィルター
 */
function customFilter(
  item: InquiryResponse,
  s: InquirySearchParser,
  currentMemberId?: number | null,
  currentDivisionId?: number | null,
): boolean {
  // キーワード検索（含む）
  for (const query of s.queries) {
    if (!execSearchQuery(item, query, SEARCHABLE_KEYS, NAME_KEYS)) return false
  }

  // キーワード検索（除外）
  for (const exclude of s.excludes) {
    if (execSearchQuery(item, exclude, SEARCHABLE_KEYS, NAME_KEYS)) return false
  }

  // 主管部門フィルター
  if (s.primaryDivisionId && item.primary_division_id !== s.primaryDivisionId) return false

  // 自身の担当フィルター
  if (s.isMyAssignment && currentMemberId) {
    const isMyDeal =
      item.sales_member_id === currentMemberId ||
      item.service_manager_member_id === currentMemberId ||
      item.coordination_member_id === currentMemberId
    if (!isMyDeal) return false
  }

  // 自分の部署フィルター
  if (s.isMyDivision && currentDivisionId) {
    if (item.primary_division_id !== currentDivisionId) return false
  }

  // 商談未紐付けフィルター
  if (s.isNoDeal && item.deal_id !== null) return false

  // 営業担当フィルター（部分一致）
  if (s.salesMemberName) {
    const name = (item.sales_member_name ?? '').replace(/\s/g, '')
    if (!name.toLowerCase().includes(s.salesMemberName.replace(/\s/g, '').toLowerCase())) return false
  }

  // SMG担当フィルター（部分一致）
  if (s.serviceManagerName) {
    const name = (item.service_manager_name ?? '').replace(/\s/g, '')
    if (!name.toLowerCase().includes(s.serviceManagerName.replace(/\s/g, '').toLowerCase())) return false
  }

  // ステータスフィルター
  if (s.inquiryStatusNames.length > 0 && !s.inquiryStatusNames.includes(item.inquiry_assign_status_name)) return false

  // 作業場所フィルター（選択された作業場所のいずれかに該当すればOK）
  const locationFilters: boolean[] = []
  if (s.workLocation.onsite) locationFilters.push(item.is_onsite)
  if (s.workLocation.offsite) locationFilters.push(item.is_offsite)
  if (s.workLocation.remote) locationFilters.push(item.is_remote)
  if (locationFilters.length > 0 && !locationFilters.some(Boolean)) return false

  // 募集形態フィルター（未設定の引合は、どちらを指定しても該当しない）
  if (s.recruitmentType && item.recruitment_type !== s.recruitmentType) return false

  // 開始時期フィルター（開始月・なる早）。両方指定したときは「開始月に一致、またはなる早」の OR で絞る。
  // なる早の引合は開始日を持たないため、AND にすると必ず 0 件になる
  const startFilters: boolean[] = []
  if (s.startMonth) startFilters.push(matchesStartMonth(item.start_on, s.startMonth))
  if (s.isStartAsap) startFilters.push(item.is_start_asap)
  if (startFilters.length > 0 && !startFilters.some(Boolean)) return false

  // 想定金額（下限）フィルター
  if (s.estimatedAmountMin) {
    const min = Number(s.estimatedAmountMin)
    if (!Number.isNaN(min) && (item.estimated_amount === null || item.estimated_amount < min)) return false
  }

  // 想定金額（上限）フィルター
  if (s.estimatedAmountMax) {
    const max = Number(s.estimatedAmountMax)
    if (!Number.isNaN(max) && (item.estimated_amount === null || item.estimated_amount > max)) return false
  }

  return true
}
